import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WorkflowGraphStore } from '../../store/workflow-graph.store';
import { WorkflowEditorService } from '../../store/workflow-editor.service';
import { WORKFLOW_NODE_TYPE_META } from '../../models/node-catalog';
import { WorkflowNodeCatalogItemResponse, WorkflowNodeType } from '../../../models/workflow-builder.model';

/** MIME del arrastre de un ítem de la paleta al canvas (lo lee `flow-canvas` en su `drop`). */
export const WORKFLOW_PALETTE_DRAG_MIME = 'application/x-workflow-node';

/** Parámetros de creación que viajan en el arrastre (o que usa el clic directo). */
export interface WorkflowPaletteAddPayload {
  nodeType: WorkflowNodeType;
  nodeSubtype: string | null;
  title: string;
  description: string | null;
  config: Record<string, unknown>;
}

/** Grupo de la paleta: una categoría del catálogo con sus ítems. */
interface PaletteGroup {
  label: string;
  items: WorkflowNodeCatalogItemResponse[];
}

/**
 * Paleta de pasos del builder de workflow (Fase 2). Absorbe `workflow-node-catalog`:
 * lista los tipos/subtipos que expone el backend (`WorkflowNodeCatalogResponse`, vía
 * `WorkflowEditorService.catalog()`) agrupados por su categoría, y usa `WORKFLOW_NODE_TYPE_META`
 * solo para lo estable por tipo (icono/color).
 *
 * Agregar un paso se hace de dos formas, ambas terminan en `store.addNode(...)`:
 * - CLIC: crea el nodo en una posición escalonada y programa el autosave.
 * - ARRASTRE: el canvas resuelve el punto de caída (viewport de ngx-vflow) y crea el nodo.
 *
 * Reactividad SOLO por signals del store/editor (gotcha §6): la paleta se deshabilita en
 * modo lectura (`editor.readOnly()`) y deshabilita el disparador cuando ya existe uno.
 */
@Component({
  selector: 'app-workflow-node-palette',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './node-palette.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowNodePaletteComponent {
  private readonly store = inject(WorkflowGraphStore);
  private readonly editor = inject(WorkflowEditorService);

  protected readonly meta = WORKFLOW_NODE_TYPE_META;
  protected readonly search = signal('');

  protected readonly readOnly = computed(() => this.editor.readOnly());
  protected readonly hasTrigger = this.store.hasTriggerNode;
  protected readonly loading = this.editor.loading;

  /** Ítems del catálogo agrupados por categoría y filtrados por el texto de búsqueda. */
  protected readonly groups = computed<PaletteGroup[]>(() => {
    const term = this.search().trim().toLowerCase();
    const isContactWorkflow = this.editor.workflow()?.objectType === 'CONTACT';
    const byCategory = new Map<string, WorkflowNodeCatalogItemResponse[]>();
    for (const item of this.editor.catalog()) {
      // Las notas se ofrecen aparte (no vienen siempre en el catálogo).
      if (item.nodeType === 'ANNOTATION') {
        continue;
      }
      // "Cambio de ciclo o etapa" solo casa con eventos de CONTACTO. El filtro `objectType` del
      // endpoint del catálogo es un no-op en el backend, así que se filtra en cliente.
      if (item.nodeSubtype === 'LIFECYCLE_CHANGED' && !isContactWorkflow) {
        continue;
      }
      if (term) {
        const haystack = `${item.label} ${item.description ?? ''} ${item.category} ${item.nodeSubtype}`.toLowerCase();
        if (!haystack.includes(term)) {
          continue;
        }
      }
      const key = item.category || item.nodeType;
      const list = byCategory.get(key) ?? [];
      list.push(item);
      byCategory.set(key, list);
    }
    return [...byCategory.entries()].map(([label, items]) => ({ label, items }));
  });

  /** Ítem sintético para agregar una nota visual (no depende del catálogo del backend). */
  protected readonly annotationItem: WorkflowPaletteAddPayload = {
    nodeType: 'ANNOTATION',
    nodeSubtype: 'TEXT_NOTE',
    title: 'Nota',
    description: null,
    config: { text: '' }
  };

  protected isDisabled(item: WorkflowNodeCatalogItemResponse): boolean {
    return this.readOnly() || !item.enabled || (item.nodeType === 'TRIGGER' && this.hasTrigger());
  }

  protected tooltip(item: WorkflowNodeCatalogItemResponse): string {
    if (this.readOnly()) {
      return 'No se puede editar el workflow en este momento.';
    }
    if (item.nodeType === 'TRIGGER' && this.hasTrigger()) {
      return 'El workflow ya tiene un disparador. Solo puede haber uno.';
    }
    if (!item.enabled) {
      return item.reason || 'Este paso no está disponible.';
    }
    return item.description || item.label;
  }

  // ── Clic → agregar en posición escalonada ─────────────────────────────────

  protected onItemClick(item: WorkflowNodeCatalogItemResponse): void {
    if (this.isDisabled(item)) {
      return;
    }
    this.addAt(this.toPayload(item), this.nextPosition());
  }

  protected onAnnotationClick(): void {
    if (this.readOnly()) {
      return;
    }
    this.addAt(this.annotationItem, this.nextPosition());
  }

  // ── Arrastre → el canvas resuelve el punto y crea el nodo ──────────────────

  protected onDragStart(event: DragEvent, payload: WorkflowPaletteAddPayload, disabled: boolean): void {
    if (disabled || !event.dataTransfer) {
      event.preventDefault();
      return;
    }
    const raw = JSON.stringify(payload);
    event.dataTransfer.setData(WORKFLOW_PALETTE_DRAG_MIME, raw);
    // text/plain de respaldo: algunos navegadores exigen un tipo estándar.
    event.dataTransfer.setData('text/plain', raw);
    event.dataTransfer.effectAllowed = 'copy';
  }

  protected toPayload(item: WorkflowNodeCatalogItemResponse): WorkflowPaletteAddPayload {
    return {
      nodeType: item.nodeType,
      nodeSubtype: item.nodeSubtype || null,
      title: item.label,
      description: item.description ?? null,
      config: (item.defaultConfig as Record<string, unknown> | undefined) ?? {}
    };
  }

  /** Posición escalonada para el alta por clic (el arrastre usa el cursor). */
  private nextPosition(): { x: number; y: number } {
    const count = this.store.nodes().length;
    return { x: 160 + (count % 4) * 48, y: 120 + count * 96 };
  }

  private addAt(payload: WorkflowPaletteAddPayload, position: { x: number; y: number }): void {
    this.store.addNode(payload.nodeType, payload.nodeSubtype, position.x, position.y, {
      title: payload.title,
      description: payload.description,
      config: payload.config
    });
    this.editor.queueAutosave();
  }
}
